import React, { useState, useEffect, useRef } from 'react';
import AdminNav from '../components/AdminNav';
import {
  getLocations, getShiftTimes,
  subscribeToAssignments, subscribeToPortalSettings,
  savePortalSettings, updateAssignmentSettings,
} from '../services/firebase';
import './Admin.css';

const DAYS = [
  { key: 'Thu-April-9',  label: 'Thursday, April 9' },
  { key: 'Fri-April-10', label: 'Friday, April 10' },
  { key: 'Sat-April-11', label: 'Saturday, April 11' },
  { key: 'Sun-April-12', label: 'Sunday, April 12' },
];

function SchedulingSettings({ onLogout }) {
  const [locations, setLocations] = useState([]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [portalSettings, setPortalSettings] = useState({ selfSchedulingEnabled: false });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(DAYS[0].key);
  const [saving, setSaving] = useState({});
  const [bulkWorking, setBulkWorking] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [globalSaving, setGlobalSaving] = useState(false);
  const unsubAssign = useRef(null);
  const unsubPortal = useRef(null);

  useEffect(() => {
    const init = async () => {
      const [locs, shifts] = await Promise.all([getLocations(), getShiftTimes()]);
      setLocations(locs.filter(l => l.active).map(l => l.name).sort());
      setShiftTimes(shifts.filter(s => s.active).sort((a, b) => a.order - b.order));
      setLoading(false);
    };
    init();

    // Live subscriptions — reflects true Firestore state immediately
    unsubAssign.current = subscribeToAssignments(data => setAssignments(data));
    unsubPortal.current = subscribeToPortalSettings(data => setPortalSettings(data));

    return () => {
      if (unsubAssign.current) unsubAssign.current();
      if (unsubPortal.current) unsubPortal.current();
    };
  }, []);

  // Build fast lookup map
  const assignMap = {};
  assignments.forEach(a => {
    assignMap[`${a.day}_${a.location}_${a.shiftTime}`] = a;
  });
  const getA = (day, loc, shiftName) => assignMap[`${day}_${loc}_${shiftName}`] || {};

  const countLocalFills = (a) => {
    const vols = a.volunteers || [];
    const cap = a.localCap || 2;
    let n = 0;
    for (let i = 3; i < 3 + cap; i++) { if (vols[i]) n++; }
    return n;
  };

  const openCount = assignments.filter(a => a.schedulingOpen).length;

  const handleToggleGlobal = async () => {
    const next = !portalSettings.selfSchedulingEnabled;
    setGlobalError('');
    setGlobalSaving(true);
    const result = await savePortalSettings({ selfSchedulingEnabled: next });
    setGlobalSaving(false);
    if (!result.success) {
      setGlobalError(`Failed to save: ${result.error || 'unknown error'}. Check Firestore rules.`);
    }
    // State updates via live subscription — no optimistic update needed
  };

  const applyLocal = (day, loc, shiftName, patch) => {
    setAssignments(prev => {
      const id = `${day}_${loc}_${shiftName}`.replace(/\s+/g, '_');
      const idx = prev.findIndex(x => x.id === id);
      if (idx === -1) {
        return [...prev, { id, shiftId: id, day, location: loc, shiftTime: shiftName, volunteers: [], localCap: 2, schedulingOpen: false, ...patch }];
      }
      return prev.map((x, i) => i === idx ? { ...x, ...patch } : x);
    });
  };

  const handleToggleOpen = async (loc, shift, currentOpen) => {
    const key = `${selectedDay}_${loc}_${shift.name}`;
    setSaving(s => ({ ...s, [key]: true }));
    const a = getA(selectedDay, loc, shift.name);
    const cap = a.localCap || 2;
    const next = !currentOpen;
    applyLocal(selectedDay, loc, shift.name, { schedulingOpen: next });
    await updateAssignmentSettings(selectedDay, loc, shift.name, { localCap: cap, schedulingOpen: next });
    setSaving(s => ({ ...s, [key]: false }));
  };

  const handleSetCap = async (loc, shift, cap) => {
    const key = `${selectedDay}_${loc}_${shift.name}`;
    setSaving(s => ({ ...s, [key]: true }));
    const a = getA(selectedDay, loc, shift.name);
    applyLocal(selectedDay, loc, shift.name, { localCap: cap });
    await updateAssignmentSettings(selectedDay, loc, shift.name, { localCap: cap, schedulingOpen: a.schedulingOpen || false });
    setSaving(s => ({ ...s, [key]: false }));
  };

  const handleBulk = async (open) => {
    setBulkWorking(true);
    const dayShifts = shiftTimes.filter(s => !s.days || s.days.includes(selectedDay));
    for (const loc of locations) {
      for (const shift of dayShifts) {
        const a = getA(selectedDay, loc, shift.name);
        applyLocal(selectedDay, loc, shift.name, { schedulingOpen: open });
        await updateAssignmentSettings(selectedDay, loc, shift.name, {
          localCap: a.localCap || 2,
          schedulingOpen: open,
        });
      }
    }
    setBulkWorking(false);
  };

  if (loading) {
    return (
      <div className="admin-container">
        <AdminNav onLogout={onLogout} />
        <div className="loading">Loading…</div>
      </div>
    );
  }

  const dayShifts = shiftTimes.filter(s => !s.days || s.days.includes(selectedDay));

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Scheduling Settings</h1>
        <p className="page-subtitle">
          Control when volunteers can self-schedule. Set local slot caps and open/close individual shifts.
        </p>
      </header>

      <div className="scheduling-content">

        {/* Global toggle */}
        <div className="ss-global-card">
          <div className="ss-global-info">
            <strong>Volunteer Self-Scheduling</strong>
            <p>
              {portalSettings.selfSchedulingEnabled
                ? `Open — volunteers can sign up for shifts. ${openCount} shift${openCount !== 1 ? 's' : ''} currently open.`
                : 'Closed — volunteers cannot sign up. Turn this on when ready.'}
            </p>
            {globalError && (
              <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>{globalError}</p>
            )}
          </div>
          <button
            className={`ss-global-toggle${portalSettings.selfSchedulingEnabled ? ' on' : ''}`}
            onClick={handleToggleGlobal}
            disabled={globalSaving}
          >
            {globalSaving ? '…' : portalSettings.selfSchedulingEnabled ? 'ON' : 'OFF'}
          </button>
        </div>

        {/* Day tabs */}
        <div className="dash-day-tabs" style={{ marginBottom: 0 }}>
          {DAYS.map(d => (
            <button
              key={d.key}
              className={`day-tab${selectedDay === d.key ? ' active' : ''}`}
              onClick={() => setSelectedDay(d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Bulk actions */}
        <div className="ss-bulk-row">
          <span>Bulk for this day:</span>
          <button className="btn btn-secondary" onClick={() => handleBulk(true)} disabled={bulkWorking}>
            {bulkWorking ? '…' : 'Open All'}
          </button>
          <button className="btn btn-secondary" onClick={() => handleBulk(false)} disabled={bulkWorking}>
            {bulkWorking ? '…' : 'Close All'}
          </button>
        </div>

        {/* Grid */}
        {locations.length > 0 && dayShifts.length > 0 ? (
          <div
            className="ss-grid"
            style={{ gridTemplateColumns: `180px repeat(${dayShifts.length}, 1fr)` }}
          >
            <div className="ss-header-cell" />
            {dayShifts.map(st => (
              <div key={st.id} className="ss-header-cell">{st.name}</div>
            ))}

            {locations.map(loc => (
              <React.Fragment key={loc}>
                <div className="ss-loc-cell">{loc}</div>
                {dayShifts.map(st => {
                  const a = getA(selectedDay, loc, st.name);
                  const cap = a.localCap || 2;
                  const isOpen = a.schedulingOpen || false;
                  const fills = countLocalFills(a);
                  const key = `${selectedDay}_${loc}_${st.name}`;
                  const busy = saving[key];

                  return (
                    <div key={st.id} className={`ss-cell${isOpen ? ' ss-cell-open' : ' ss-cell-closed'}`}>
                      <button
                        className={`ss-open-btn${isOpen ? ' open' : ''}`}
                        onClick={() => handleToggleOpen(loc, st, isOpen)}
                        disabled={busy}
                      >
                        {isOpen ? 'Open' : 'Closed'}
                      </button>
                      <div className="ss-cell-footer">
                        <select
                          className="ss-cap-select"
                          value={cap}
                          onChange={e => handleSetCap(loc, st, Number(e.target.value))}
                          disabled={busy}
                        >
                          <option value={2}>2 slots</option>
                          <option value={3}>3 slots</option>
                        </select>
                        <span className={`ss-fills${fills >= cap ? ' full' : ''}`}>
                          {fills}/{cap}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="no-data">No active locations or shift times found. Add them in Settings first.</div>
        )}
      </div>
    </div>
  );
}

export default SchedulingSettings;
